import { useMemo } from 'react';
import Moment from 'moment/moment';
import { useParams } from 'react-router-dom';
import { useAllow } from '../../../../../../../components/hooks/useAllow';
import { ProductTypes, type ProductType } from '../../../../../../../types';

/**
 * remove back date selection constraints for Underwriter. as they have their own Underwriting principles.
 */

/**
 * The effective dates a quote may take: from 7 days back to 75 ahead, ZSP from 5 back to 90 ahead; none for an
 * underwriter (`accounts:uw`). Shared with the broker experience's quote options.
 */
export function effectiveDateBounds(
  productType: ProductType | string | undefined,
  canUnderwriteAccounts: boolean,
  now: Moment.Moment = Moment()
): { minDate: Date; maxDate: Date } | undefined {
  if (canUnderwriteAccounts) {
    return undefined;
  }
  if (productType === ProductTypes.zsp) {
    return {
      minDate: now.clone().subtract(5, 'd').toDate(),
      maxDate: now.clone().add(90, 'd').toDate(),
    };
  }
  return {
    minDate: now.clone().subtract(7, 'd').toDate(),
    maxDate: now.clone().add(75, 'd').toDate(),
  };
}

const useEffectiveDateConstraints = () => {
  const params = useParams<{ productType: ProductType }>();
  const CAN_UW_ACCOUNTS = useAllow(['accounts:uw']);
  const bounds = effectiveDateBounds(params.productType, CAN_UW_ACCOUNTS);
  const minTime = bounds?.minDate.getTime();
  const maxTime = bounds?.maxDate.getTime();

  return useMemo(() => {
    if (minTime === undefined || maxTime === undefined) {
      return;
    }
    return {
      minDate: new Date(minTime),
      maxDate: new Date(maxTime),
    };
  }, [minTime, maxTime]);
};

export default useEffectiveDateConstraints;
